import * as io from "io-ts";
import dynamic from "next/dynamic";
import React, { type PropsWithChildren } from "react";
import { IntlProvider } from "react-intl";
import { InvariantViolation } from "../lib/exception";
export const DEFAULT_LOCALE: Locale = "en";

export const Locale = io.keyof({
  en: null,
  fr: null,
  es: null,
  ru: null,
  ar: null,
  zh: null,
  de: null,
});

export type Locale = io.TypeOf<typeof Locale>;

export function foldLocale<A>(pattern: { [K in Locale]: A }): (locale: Locale) => A {
  return (locale: Locale) => pattern[locale];
}

export function foldLocaleL<A>(pattern: { [K in Locale]: () => A }): (locale: Locale) => A {
  return (locale: Locale) => pattern[locale]();
}

export type LocaleDir = "ltr" | "rtl";

export interface LocaleMeta {
  lang: Locale;
  name: string;
  dir: LocaleDir;
}

export const locales: Array<LocaleMeta> = [
  {
    lang: "en",
    name: "English",
    dir: "ltr",
  },
  {
    lang: "fr",
    name: "Français",
    dir: "ltr",
  },
  {
    lang: "es",
    name: "Español",
    dir: "ltr",
  },
  {
    lang: "ru",
    name: "Русский",
    dir: "ltr",
  },
  {
    lang: "ar",
    name: "العربية",
    dir: "rtl",
  },
  {
    lang: "zh",
    name: "中文",
    dir: "ltr",
  },
  {
    lang: "de",
    name: "Deutsch",
    dir: "ltr",
  },
];

export const lookupLocale = (locale: Locale) => {
  if ((locale as any) === "default") {
    return locales[0];
  }

  const result = locales.find((x) => x.lang === locale);
  if (result != null) {
    return result;
  } else {
    throw new InvariantViolation(`Could not find locale metadata for ID "${locale}"`);
  }
};

// -----------------------------------------------------------------------------
// Locale Provider

const mkLocaleProvider = foldLocale<React.ComponentType<PropsWithChildren>>({
  ar: dynamic(() => import("./ar/messages.json").then(mkI18nProvider("ar"))),
  de: dynamic(() => import("./de/messages.json").then(mkI18nProvider("de"))),
  en: dynamic(() => import("./en/messages.json").then(mkI18nProvider("en"))),
  es: dynamic(() => import("./es/messages.json").then(mkI18nProvider("es"))),
  fr: dynamic(() => import("./fr/messages.json").then(mkI18nProvider("fr"))),
  ru: dynamic(() => import("./ru/messages.json").then(mkI18nProvider("ru"))),
  zh: dynamic(() => import("./zh/messages.json").then(mkI18nProvider("zh"))),
});

export const LocaleProvider = ({ locale, ...props }: PropsWithChildren<{ locale: Locale }>) => {
  const Provider = React.useMemo(() => mkLocaleProvider(locale), [locale]);
  return React.createElement(Provider, props);
};

const mkI18nProvider =
  (locale: Locale) =>
  (m: any) =>
  ({ children }: PropsWithChildren) => {
    const messages = m.default || m;
    return (
      <IntlProvider locale={locale} messages={messages}>
        {children}
      </IntlProvider>
    );
  };
